// web/js/player-approval/approvalManager.ts

import type { ApprovalPanelProps, ApprovalPanelHandle, ApprovalHistoryItem, ReviewerInfo } from './types';
import type { Snapshot } from '../player-columns/types';

declare const moment: any;

/** Shape returned by GET /file/{fileId}/approval */
interface ApprovalApiResponse {
  workflow_id: number;
  workflow_name: string;
  is_approval_workflow: boolean;
  users_to_approve: { id: number; name: string; avatar_url: string }[];
  approve_button_text: string;
  reject_button_text: string;
  user_can_approve: boolean;
  history: {
    user_id: number;
    name: string;
    avatar_url: string;
    approved_status: boolean;
    comment: string | null;
    date: number;
  }[];
}

function getLinkId(): string | null {
  const w = window as any;
  return w.jsconnect?.link?.id ? String(w.jsconnect.link.id) : null;
}

function getCurrentUser(): { id: number; name: string } | null {
  const w = window as any;
  const u = w.jsconnect?.user;
  if (!u || !u.id) return null;
  return { id: Number(u.id), name: String(u.name ?? '') };
}

function mapResponseToProps(
  fileId: number,
  data: ApprovalApiResponse,
  user: { id: number; name: string },
  onApprovalChange: (fileId: number, approved: boolean, newWorkflowId?: number) => void,
): ApprovalPanelProps {
  const history: ApprovalHistoryItem[] = data.history.map(h => ({
    userId: h.user_id,
    userName: h.name,
    avatarUrl: h.avatar_url,
    approved: h.approved_status,
    comment: h.comment ?? '',
    date: h.date ?? (Date.now() / 1000),
  }));

  const remainingReviewers: ReviewerInfo[] = data.users_to_approve.map(r => ({
    userId: r.id,
    userName: r.name,
    avatarUrl: r.avatar_url,
    isCurrentUser: r.id === user.id,
  }));

  const hasAlreadyReviewed = data.history.some(h => h.user_id === user.id);

  // Note: `beforeApprovalChange` is intentionally not set. The PHP version
  // did not set it either — it is an optional hook for future use.
  return {
    fileId,
    workflowName: data.is_approval_workflow ? data.workflow_name : null,
    canApprove: data.user_can_approve,
    hasAlreadyReviewed,
    approveTitle: data.approve_button_text,
    rejectTitle: data.reject_button_text,
    userName: user.name,
    userId: user.id,
    history,
    remainingReviewers,
    onApprovalChange,
  };
}

let currentHandle: ApprovalPanelHandle | null = null;
let currentFileId: number | null = null;
let fetchGeneration = 0;

function unmountPanel(): void {
  if (currentHandle) {
    try { currentHandle.unmount(); } catch (_) {}
    currentHandle = null;
  }
}

async function fetchAndMount(fileId: number): Promise<void> {
  const generation = ++fetchGeneration;
  currentFileId = fileId;

  const user = getCurrentUser();
  if (!user) {
    unmountPanel();
    return;
  }

  let data: ApprovalApiResponse;
  try {
    const resp = await fetch(`/file/${fileId}/approval`);
    if (generation !== fetchGeneration) return; // stale
    if (!resp.ok) {
      unmountPanel();
      return;
    }
    data = await resp.json();
  } catch {
    if (generation !== fetchGeneration) return;
    unmountPanel();
    return;
  }

  if (generation !== fetchGeneration) return; // stale

  const shouldShow = data.is_approval_workflow || data.history.length > 0;
  if (!shouldShow) {
    unmountPanel();
    return;
  }

  const onApprovalChange = (_fid: number, _approved: boolean, newWorkflowId?: number) => {
    if (newWorkflowId != null && currentFileId != null) {
      fetchAndMount(currentFileId);
    }
  };

  const props = mapResponseToProps(fileId, data, user, onApprovalChange);

  unmountPanel();

  if (typeof window.mountApprovalPanel === 'function') {
    currentHandle = window.mountApprovalPanel('player-approval-container', props);
  }
}

function onColumnChange(snapshot: Snapshot): void {
  const linkId = getLinkId();
  const fileId = snapshot.activeFileId;

  if (linkId || !fileId) {
    unmountPanel();
    currentFileId = null;
    return;
  }

  if (fileId === currentFileId) return; // already fetching or mounted

  fetchAndMount(fileId);
}

export function initApprovalManager(): void {
  const w = window as any;

  const trySubscribe = () => {
    if (!w.playerColumns || typeof w.playerColumns.subscribe !== 'function') return;

    // Initial fetch
    const snap = w.playerColumns.getSnapshot();
    if (snap) onColumnChange(snap);

    // Subscribe to future changes
    w.playerColumns.subscribe(onColumnChange);
  };

  // If columns are already ready, subscribe now; otherwise wait for the event.
  if (w.playerColumns && typeof w.playerColumns.getSnapshot === 'function') {
    trySubscribe();
  } else {
    window.addEventListener('player-columns-ready', trySubscribe, { once: true });
  }
}

/**
 * Re-fetch and re-render the approval panel for the currently focused file.
 * No-op if no file is focused or if in a shared-link context.
 */
export function refreshApprovalPanel(): void {
  if (currentFileId != null) {
    fetchAndMount(currentFileId);
  }
}

// Expose globally so legacy JS (player_utils.js, player.php, etc.) can call these.
(window as any).initApprovalManager = initApprovalManager;
(window as any).refreshApprovalPanel = refreshApprovalPanel;
